import { FC, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardHeader,
  Checkbox,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Popover,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Close,
  KeyboardArrowDown,
  KeyboardArrowUp,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import type {
  ChoiceGroupQuestion,
  Cut,
  CutsheetCatalog,
  Primal,
  Specification,
  Trim,
} from '../../types';
import type { ResponseSpecificationSelection } from './types';
import { useQuestionnaireResponse } from './questionnaire-response-context';

// ── Types ─────────────────────────────────────────────────────────────────────

/**
 * Resolved print settings used for controlling the layout.
 * All fields have defaults so every property is always defined internally.
 */
export interface AdvancedViewPrintSettings {
  /** Number of columns in the primals/cuts grid (1–3) */
  primalColumns: number;
  /** Where the specifications sidebar is placed relative to the primals grid */
  specsLayoutPosition: 'top' | 'bottom' | 'left' | 'right' | 'none';
  /** Whether to show the group name beneath the primal title */
  showPrimalGroup: boolean;
  /** Whether to always show the primal notes input, even when empty */
  alwaysShowPrimalNotes: boolean;
  /** Primal group IDs to hide from the printed cutsheet */
  hiddenGroupIds: string[];
  /** Individual cut/option IDs to hide from the printed cutsheet */
  hiddenItemIds: string[];
  /** Specification IDs to hide from the printed cutsheet sidebar */
  hiddenSpecificationIds: string[];
}

/** Defaults used when no saved settings are available. */
const DEFAULT_PRINT_SETTINGS: AdvancedViewPrintSettings = {
  primalColumns: 2,
  specsLayoutPosition: 'right',
  showPrimalGroup: true,
  alwaysShowPrimalNotes: true,
  hiddenGroupIds: [],
  hiddenItemIds: [],
  hiddenSpecificationIds: [],
};

export interface AdvancedViewStepProps {
  /** PDF layout settings — controls column count, sidebar placement, etc. */
  printSettings?: Partial<AdvancedViewPrintSettings>;
  /**
   * When false, cuts that are not referenced by any questionnaire question are
   * still interactive (not greyed out or disabled). Useful in settings contexts
   * where pre-selected cuts can be freely chosen regardless of the questionnaire.
   * Defaults to true.
   */
  disableNonQuestionnaireCuts?: boolean;
  /**
   * When true, hides the settings/gear icons on cut and trim rows. Used by the
   * read-only preview where specs are surfaced inline and editing is disabled.
   */
  hideSpecSettings?: boolean;
}

// ── Sub-components ────────────────────────────────────────────────────────────

/**
 * Spec field inside the popover — renders the appropriate input based on the
 * spec's inputType (text, number, select, multiselect).
 * @param root0
 * @param root0.spec
 * @param root0.value
 * @param root0.onChange
 */
const SpecPopoverField: FC<{
  spec: Specification;
  value: string;
  onChange: (value: string) => void;
}> = ({ spec, value, onChange }) => {
  if (spec.inputType === 'select' || spec.inputType === 'multiselect') {
    // options may be plain strings or rich SpecificationOption objects
    const rawOptions = (spec.options ?? []) as
      | string[]
      | Array<{ label: string; value: string }>;
    const normalizedOptions = rawOptions.map((opt) =>
      typeof opt === 'string' ? { label: opt, value: opt } : opt,
    );
    return (
      <FormControl fullWidth size="small">
        <InputLabel>{spec.title}</InputLabel>
        <Select
          value={value}
          label={spec.title}
          onChange={(e) => onChange(e.target.value as string)}
        >
          <MenuItem value="">
            <em>None</em>
          </MenuItem>
          {normalizedOptions.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }
  return (
    <TextField
      fullWidth
      size="small"
      label={spec.title}
      type={spec.inputType === 'number' ? 'number' : 'text'}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      InputProps={
        spec.unit
          ? {
              endAdornment: (
                <Typography
                  variant="caption"
                  sx={{ ml: 0.5, whiteSpace: 'nowrap' }}
                >
                  {spec.unit}
                </Typography>
              ),
            }
          : undefined
      }
    />
  );
};

/**
 * A single cut row inside a primal table: checkbox + cut name with
 * strikethrough styling when the cut is deselected (mirrors the PDF template).
 * When the cut has assigned specifications a settings icon opens a popover.
 * @param root0
 * @param root0.cut
 * @param root0.assignedSpecs
 * @param root0.specValues
 * @param root0.onSpecValueChange
 * @param root0.isSelected
 * @param root0.onToggle
 * @param root0.isInQuestionnaire
 * @param root0.choiceGroupDisabledReason
 */
const CutRow: FC<{
  cut: Cut;
  assignedSpecs: Specification[];
  specValues: Record<string, string>;
  onSpecValueChange: (specId: string, value: string) => void;
  isSelected: boolean;
  onToggle: () => void;
  /** False when this cut is not referenced by any questionnaire question — rendered disabled. */
  isInQuestionnaire: boolean;
  /**
   * When set, this cut belongs to a choice-group option that conflicts with a
   * currently selected option. The string is shown as a hover tooltip.
   */
  choiceGroupDisabledReason?: string;
  /** When true, the settings icon is hidden entirely (preview mode). */
  hideSpecSettings?: boolean;
}> = ({
  cut,
  assignedSpecs,
  specValues,
  onSpecValueChange,
  isSelected,
  onToggle,
  isInQuestionnaire,
  choiceGroupDisabledReason,
  hideSpecSettings,
}) => {
  // Popover anchor for the specs settings panel
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const popoverOpen = Boolean(anchorEl);
  const hasSpecs = assignedSpecs.length > 0;
  const anySpecSet = Object.values(specValues).some(Boolean);
  const isChoiceGroupDisabled = Boolean(choiceGroupDisabledReason);

  /**
   * Resolve display labels for filled-in spec values so they can be shown
   * pipe-separated after the cut name (e.g. "Ribeye | Boneless | 1 inch").
   */
  const specLabels = assignedSpecs
    .map((spec) => {
      const val = specValues[spec.id];
      if (!val) return null;
      if (spec.inputType === 'select' || spec.inputType === 'multiselect') {
        const rawOptions = (spec.options ?? []) as
          | string[]
          | Array<{ label: string; value: string }>;
        const match = rawOptions.find(
          (o) => (typeof o === 'string' ? o : o.value) === val,
        );
        return match ? (typeof match === 'string' ? match : match.label) : val;
      }
      return val;
    })
    .filter((v): v is string => Boolean(v));

  const isAnyDisabled = !isInQuestionnaire || isChoiceGroupDisabled;

  const disabledTooltip = isChoiceGroupDisabled
    ? choiceGroupDisabledReason
    : !isInQuestionnaire
      ? 'This cut is not part of the questionnaire'
      : undefined;

  const tableRow = (
    <TableRow
      sx={{
        // Selected+disabled rows stay more visible so the checked state reads clearly
        opacity: isAnyDisabled ? (isSelected ? 0.65 : 0.38) : 1,
        // Keep pointer-events active so Tooltip hover works; interactivity is
        // blocked via the disabled Checkbox instead.
        cursor: isAnyDisabled ? 'not-allowed' : 'default',
      }}
    >
      <TableCell sx={{ width: 36, textAlign: 'center', p: 0.5 }}>
        <Checkbox
          checked={isSelected}
          onChange={onToggle}
          disabled={isAnyDisabled}
          size="small"
          sx={{
            p: 0,
            pointerEvents: isAnyDisabled ? 'none' : 'auto',
            // Keep the checked+disabled checkbox visually solid
            '&.Mui-checked.Mui-disabled': {
              color: 'primary.main',
              opacity: 1,
            },
          }}
          inputProps={{
            'aria-label': `Select ${cut.printedText || cut.title}`,
          }}
        />
      </TableCell>
      <TableCell
        sx={{
          fontWeight: isSelected ? 'bold' : 'normal',
          color: isSelected ? 'text.primary' : 'text.disabled',
          textDecoration: isSelected ? 'none' : 'line-through',
          fontSize: '0.8rem',
          p: 0.5,
        }}
      >
        {cut.printedText || cut.title}
        {specLabels.length > 0 && (
          <Typography
            component="span"
            variant="caption"
            sx={{ opacity: 0.75, ml: 0.5 }}
          >
            {specLabels.map((l) => `| ${l}`).join(' ')}
          </Typography>
        )}
      </TableCell>
      {/* Settings column — always rendered so the table column widths stay consistent */}
      {!hideSpecSettings && (
        <TableCell sx={{ width: 32, p: 0.25, textAlign: 'center' }}>
          {hasSpecs && (
            <IconButton
              size="small"
              onClick={(e) => setAnchorEl(e.currentTarget)}
              aria-label={`Specifications for ${cut.printedText || cut.title}`}
              sx={{ p: 0.25 }}
              // When selected: error if no specs filled, primary if specs filled.
              // When deselected: disabled — specs aren't relevant.
              disabled={!isSelected || isAnyDisabled}
              color={isSelected && !anySpecSet ? 'error' : 'primary'}
            >
              <SettingsIcon sx={{ fontSize: '0.9rem' }} />
            </IconButton>
          )}
        </TableCell>
      )}
    </TableRow>
  );

  return (
    <>
      {disabledTooltip ? (
        <Tooltip title={disabledTooltip} placement="top" arrow>
          {tableRow}
        </Tooltip>
      ) : (
        tableRow
      )}

      {/* Specs popover — shown only for cuts with assigned specs */}
      {hasSpecs && !hideSpecSettings && (
        <Popover
          open={popoverOpen}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          disableRestoreFocus
        >
          <Box sx={{ p: 2, minWidth: 240, maxWidth: 320 }}>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 'bold', mb: 1.5 }}
            >
              {cut.printedText || cut.title} — Specifications
            </Typography>
            <Stack spacing={1.5}>
              {assignedSpecs.map((spec) => (
                <SpecPopoverField
                  key={spec.id}
                  spec={spec}
                  value={specValues[spec.id] ?? ''}
                  onChange={(val) => onSpecValueChange(spec.id, val)}
                />
              ))}
            </Stack>
          </Box>
        </Popover>
      )}
    </>
  );
};

/**
 * One primal section: bold uppercase title bar, cuts table with checkboxes,
 * and an editable notes field. Mirrors `.primal-section` in the PDF template.
 * @param root0
 * @param root0.primal
 * @param root0.cuts
 * @param root0.alwaysShowNotes
 * @param root0.selectedCutIds
 * @param root0.notes
 * @param root0.onToggleCut
 * @param root0.onNoteChange
 * @param root0.specsByCutId
 * @param root0.cutSpecValues
 * @param root0.onCutSpecValueChange
 * @param root0.questionnaireCutIds
 * @param root0.disabledCutReasons
 */
const PrimalSection: FC<{
  primal: Primal;
  cuts: Cut[];
  alwaysShowNotes: boolean;
  selectedCutIds: Set<string>;
  notes: string;
  onToggleCut: (id: string) => void;
  onNoteChange: (note: string) => void;
  /** Pre-filtered specs for each cut, keyed by cut ID. */
  specsByCutId: Record<string, Specification[]>;
  /** Per-cut spec values: cutId → specId → value. */
  cutSpecValues: Record<string, Record<string, string>>;
  onCutSpecValueChange: (cutId: string, specId: string, value: string) => void;
  /** Cuts in the questionnaire — undefined means all cuts are active. */
  questionnaireCutIds?: Set<string>;
  /** Cuts disabled because another choice-group option is already selected. Maps cutId → reason. */
  disabledCutReasons?: Record<string, string>;
  /** When true, hides the settings/gear icon on each cut row. */
  hideSpecSettings?: boolean;
}> = ({
  primal,
  cuts,
  alwaysShowNotes,
  selectedCutIds,
  notes,
  onToggleCut,
  onNoteChange,
  specsByCutId,
  cutSpecValues,
  onCutSpecValueChange,
  questionnaireCutIds,
  disabledCutReasons,
  hideSpecSettings,
}) => (
  <Box sx={{ pb: 1.5, breakInside: 'avoid', pageBreakInside: 'avoid' }}>
    {/* Primal title — mirrors .primal-title */}
    <Box
      sx={{
        borderBottom: 2,
        borderColor: 'text.primary',
        pb: 0.4,
        mb: 0.75,
      }}
    >
      <Typography
        sx={{
          fontSize: '0.875rem',
          fontWeight: 'bold',
          textTransform: 'uppercase',
          lineHeight: 1.3,
        }}
      >
        {primal.title}
      </Typography>
    </Box>

    {/* Cuts table — mirrors .cuts-table */}
    {cuts.length > 0 ? (
      <Table
        size="small"
        sx={{
          '& td': {
            border: '1px solid',
            borderColor: 'divider',
            fontSize: '0.8rem',
          },
        }}
      >
        <TableBody>
          {cuts.map((cut) => (
            <CutRow
              key={cut.id}
              cut={cut}
              assignedSpecs={specsByCutId[cut.id] ?? []}
              specValues={cutSpecValues[cut.id] ?? {}}
              onSpecValueChange={(specId, val) =>
                onCutSpecValueChange(cut.id, specId, val)
              }
              isSelected={selectedCutIds.has(cut.id)}
              onToggle={() => onToggleCut(cut.id)}
              isInQuestionnaire={
                questionnaireCutIds ? questionnaireCutIds.has(cut.id) : true
              }
              choiceGroupDisabledReason={disabledCutReasons?.[cut.id]}
              hideSpecSettings={hideSpecSettings}
            />
          ))}
        </TableBody>
      </Table>
    ) : (
      <Typography
        sx={{
          color: 'text.disabled',
          fontStyle: 'italic',
          fontSize: '0.75rem',
          textAlign: 'center',
          py: 0.75,
        }}
      >
        No cuts available
      </Typography>
    )}

    {/* Primal notes — mirrors .primal-notes-container / .primal-notes */}
    {(alwaysShowNotes || notes) && (
      <TextField
        fullWidth
        size="small"
        variant="outlined"
        placeholder="Notes"
        value={notes}
        onChange={(e) => onNoteChange(e.target.value)}
        multiline
        maxRows={3}
        sx={{
          mt: 0.75,
          '& .MuiInputBase-input': { fontSize: '0.75rem' },
          '& .MuiOutlinedInput-root': {
            borderLeft: '3px solid',
            borderLeftColor: 'text.primary',
            borderRadius: 0,
            // backgroundColor: (theme) =>
            //   theme.palette.mode === 'dark' ? 'transparent' : '#f9f9f9',
          },
        }}
        inputProps={{ 'aria-label': `${primal.title} notes` }}
      />
    )}
  </Box>
);

/**
 * Specifications column — renders global specs with an editable value cell.
 * Mirrors `.specifications-table` from the PDF template.
 * @param root0
 * @param root0.catalog
 * @param root0.specValues
 * @param root0.onSpecValueChange
 */
const SpecsSidebar: FC<{
  catalog: CutsheetCatalog;
  specValues: Record<string, string | string[]>;
  onSpecValueChange: (specId: string, value: string | string[]) => void;
  hiddenSpecificationIds: Set<string>;
}> = ({ catalog, specValues, onSpecValueChange, hiddenSpecificationIds }) => {
  // Only global specs appear in the sidebar (item-level specs live on individual cut questions).
  // A spec is "global" when it has no assigned items.
  const globalSpecs = catalog.specifications.filter(
    (s) => !hiddenSpecificationIds.has(s.id),
  );

  return (
    <Box>
      <Typography
        sx={{
          fontSize: '0.875rem',
          fontWeight: 'bold',
          textTransform: 'uppercase',
          borderBottom: 2,
          borderColor: 'text.primary',
          pb: 0.4,
          mb: 0.75,
        }}
      >
        Specifications
      </Typography>

      {globalSpecs.length > 0 ? (
        <Table
          size="small"
          sx={{
            '& td, & th': {
              border: '1px solid',
              borderColor: 'divider',
              fontSize: '0.75rem',
              p: 0.5,
            },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  fontWeight: 'bold',
                  backgroundColor: (theme) =>
                    theme.palette.mode === 'dark' ? 'action.hover' : '#f5f5f5',
                  width: '50%',
                }}
              >
                Name
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 'bold',
                  backgroundColor: (theme) =>
                    theme.palette.mode === 'dark' ? 'action.hover' : '#f5f5f5',
                  width: '50%',
                }}
              >
                Value
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {globalSpecs.map((spec) => (
              <TableRow key={spec.id}>
                <TableCell sx={{ fontWeight: 'bold' }}>{spec.title}</TableCell>
                <TableCell sx={{ p: '2px 4px' }}>
                  {spec.inputType === 'multiselect' ? (
                    (() => {
                      const raw = specValues[spec.id];
                      const arrayValue = Array.isArray(raw)
                        ? raw
                        : raw
                          ? String(raw)
                              .split(',')
                              .map((v) => v.trim())
                              .filter(Boolean)
                          : [];
                      return (
                        <Select
                          multiple
                          value={arrayValue}
                          onChange={(e) => {
                            const next = e.target.value;
                            onSpecValueChange(
                              spec.id,
                              Array.isArray(next) ? next : [next],
                            );
                          }}
                          inputProps={{ 'aria-label': spec.title }}
                          size="small"
                          displayEmpty
                          fullWidth
                          variant="standard"
                          disableUnderline
                          sx={{ fontSize: '0.75rem' }}
                          renderValue={(selected) => {
                            const arr = selected as string[];
                            if (arr.length === 0) {
                              return (
                                <em style={{ fontSize: '0.75rem' }}>Select…</em>
                              );
                            }
                            const optionLabels = new Map<string, string>();
                            for (const opt of (spec.options ?? []) as
                              | string[]
                              | Array<{ label: string; value: string }>) {
                              if (typeof opt === 'string') {
                                optionLabels.set(opt, opt);
                              } else {
                                optionLabels.set(opt.value, opt.label);
                              }
                            }
                            return arr
                              .map((v) => optionLabels.get(v) ?? v)
                              .join(', ');
                          }}
                        >
                          {(
                            (spec.options ?? []) as
                              | string[]
                              | Array<{ label: string; value: string }>
                          ).map((opt) => {
                            const val =
                              typeof opt === 'string' ? opt : opt.value;
                            const label =
                              typeof opt === 'string' ? opt : opt.label;
                            return (
                              <MenuItem
                                key={val}
                                value={val}
                                sx={{ fontSize: '0.75rem' }}
                              >
                                <Checkbox
                                  checked={arrayValue.includes(val)}
                                  size="small"
                                  sx={{ p: 0.25, mr: 0.5 }}
                                />
                                {label}
                              </MenuItem>
                            );
                          })}
                        </Select>
                      );
                    })()
                  ) : spec.inputType === 'select' ? (
                    <Select
                      value={
                        typeof specValues[spec.id] === 'string'
                          ? (specValues[spec.id] as string)
                          : ''
                      }
                      onChange={(e) =>
                        onSpecValueChange(spec.id, e.target.value as string)
                      }
                      inputProps={{ 'aria-label': spec.title }}
                      size="small"
                      displayEmpty
                      fullWidth
                      variant="standard"
                      disableUnderline
                      sx={{ fontSize: '0.75rem' }}
                    >
                      <MenuItem value="">
                        <em style={{ fontSize: '0.75rem' }}>Select…</em>
                      </MenuItem>
                      {(
                        (spec.options ?? []) as
                          | string[]
                          | Array<{ label: string; value: string }>
                      ).map((opt) => {
                        const val = typeof opt === 'string' ? opt : opt.value;
                        const label = typeof opt === 'string' ? opt : opt.label;
                        return (
                          <MenuItem
                            key={val}
                            value={val}
                            sx={{ fontSize: '0.75rem' }}
                          >
                            {label}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  ) : (
                    <TextField
                      type={spec.inputType === 'number' ? 'number' : 'text'}
                      value={
                        typeof specValues[spec.id] === 'string'
                          ? (specValues[spec.id] as string)
                          : ''
                      }
                      onChange={(e) =>
                        onSpecValueChange(spec.id, e.target.value)
                      }
                      inputProps={{ 'aria-label': spec.title }}
                      size="small"
                      fullWidth
                      variant="standard"
                      sx={{ '& input': { fontSize: '0.75rem', p: 0 } }}
                    />
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <Typography
          sx={{
            fontSize: '0.75rem',
            color: 'text.secondary',
            fontStyle: 'italic',
          }}
        >
          No global specifications defined
        </Typography>
      )}
    </Box>
  );
};

// ── Trim section ─────────────────────────────────────────────────────────────

/**
 * TrimSection — card-based add/rank UI allowing multiple instances of the same trim.
 * Selected trims appear in a ranked table with reorder controls.
 * A single section-level notes field appears at the bottom.
 * Unselected trims are shown as clickable cards at the bottom.
 * @param root0
 * @param root0.trims
 * @param root0.selectedTrimSelections
 * @param root0.onAddTrim
 * @param root0.onRemoveTrim
 * @param root0.onMoveTrim
 * @param root0.trimSectionNotes
 * @param root0.onTrimSectionNoteChange
 * @param root0.specsByTrimId
 * @param root0.onTrimSpecValueChange
 * @param root0.onTrimWeightChange
 * @param root0.showDivider
 */
const TrimSection: FC<{
  trims: Trim[];
  /** Ordered list of selected trim selections (may contain duplicates for same trim ID). */
  selectedTrimSelections?: Array<{
    optionId: string;
    rank?: number;
    quantity?: number;
    percentage?: number;
    specifications?: ResponseSpecificationSelection[];
  }>;
  /** Whether selections are ranked (weight in lbs) or percentage-based. */
  mode: 'ranked' | 'selection_by_percentage';
  onAddTrim: (id: string) => void;
  onRemoveTrim: (index: number) => void;
  onMoveTrim: (index: number, direction: 'up' | 'down') => void;
  trimSectionNotes: string;
  onTrimSectionNoteChange: (note: string) => void;
  /** Pre-filtered specs for each trim, keyed by trim ID. */
  specsByTrimId: Record<string, Specification[]>;
  onTrimSpecValueChange: (
    trimSelectionIndex: number,
    specId: string,
    value: string,
  ) => void;
  onTrimWeightChange: (index: number, weight: number) => void;
  onTrimPercentageChange: (index: number, percentage: number) => void;
  /** When true renders a Divider above the section (default true). */
  showDivider?: boolean;
  /** When true, hides the settings/gear icon on each selected trim row. */
  hideSpecSettings?: boolean;
}> = ({
  trims,
  selectedTrimSelections,
  mode,
  onAddTrim,
  onRemoveTrim,
  onMoveTrim,
  trimSectionNotes,
  onTrimSectionNoteChange,
  specsByTrimId,
  onTrimSpecValueChange,
  onTrimWeightChange,
  onTrimPercentageChange,
  showDivider = true,
  hideSpecSettings,
}) => {
  const isPercentage = mode === 'selection_by_percentage';
  const totalPercentage = isPercentage
    ? (selectedTrimSelections ?? []).reduce(
        (sum, t) => sum + (t.percentage ?? 0),
        0,
      )
    : 0;
  // Track which row's popover is open (index or null if none)
  const [openPopoverIdx, setOpenPopoverIdx] = useState<number | null>(null);
  const [popoverAnchorEl, setPopoverAnchorEl] =
    useState<HTMLButtonElement | null>(null);

  if (trims.length === 0) return null;

  return (
    <Box sx={{ pt: showDivider ? 1.5 : 0 }}>
      {showDivider && <Divider sx={{ mb: 1.5 }} />}

      {/* Section heading — same style as primal titles */}
      <Box
        sx={{
          borderBottom: 2,
          borderColor: 'text.primary',
          pb: 0.4,
          mb: 0.75,
        }}
      >
        <Typography
          sx={{
            fontSize: '0.875rem',
            fontWeight: 'bold',
            textTransform: 'uppercase',
            lineHeight: 1.3,
          }}
        >
          Trim
        </Typography>
      </Box>

      {/* Ranked selections table */}
      {selectedTrimSelections && selectedTrimSelections.length > 0 && (
        <TableContainer sx={{ mb: 1 }}>
          <Table
            size="small"
            sx={{
              '& td, & th': {
                border: '1px solid',
                borderColor: 'divider',
                fontSize: '0.75rem',
                p: '4px 6px',
              },
            }}
          >
            <TableHead>
              <TableRow>
                {!isPercentage && (
                  <TableCell sx={{ width: 56 }}>Rank</TableCell>
                )}
                <TableCell>Trim</TableCell>
                <TableCell sx={{ width: 80 }}>
                  {isPercentage ? 'Percentage' : 'Weight (lbs)'}
                </TableCell>
                <TableCell sx={{ width: 40 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {selectedTrimSelections.map((selection, idx) => {
                const trim = trims.find((t) => t.id === selection.optionId);
                if (!trim) return null;

                const assignedSpecs = specsByTrimId[trim.id] ?? [];
                const specValues = selection.specifications
                  ? Object.fromEntries(
                      selection.specifications.map((s) => [
                        s.specification,
                        String(s.value),
                      ]),
                    )
                  : {};
                const anySpecSet = Object.values(specValues).some(Boolean);

                // Resolve display labels for filled-in spec values
                const specLabels = assignedSpecs
                  .map((spec) => {
                    const val = specValues[spec.id];
                    if (!val) return null;
                    if (
                      spec.inputType === 'select' ||
                      spec.inputType === 'multiselect'
                    ) {
                      const rawOptions = (spec.options ?? []) as
                        | string[]
                        | Array<{ label: string; value: string }>;
                      const match = rawOptions.find(
                        (o) => (typeof o === 'string' ? o : o.value) === val,
                      );
                      return match
                        ? typeof match === 'string'
                          ? match
                          : match.label
                        : val;
                    }
                    return val;
                  })
                  .filter((v): v is string => Boolean(v));

                const popoverOpen = openPopoverIdx === idx;
                const hasSpecs = assignedSpecs.length > 0;

                return (
                  <TableRow key={`${selection.optionId}-${idx}`}>
                    {/* Rank + reorder — only for ranked mode */}
                    {!isPercentage && (
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={0}>
                          <Typography
                            variant="body2"
                            fontWeight="bold"
                            sx={{ minWidth: 16, fontSize: '0.75rem' }}
                          >
                            {idx + 1}
                          </Typography>
                          <Stack direction="column" spacing={0}>
                            <IconButton
                              size="small"
                              onClick={() => onMoveTrim(idx, 'up')}
                              disabled={idx === 0}
                              sx={{ p: 0.25 }}
                            >
                              <KeyboardArrowUp sx={{ fontSize: '0.9rem' }} />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => onMoveTrim(idx, 'down')}
                              disabled={
                                idx === selectedTrimSelections.length - 1
                              }
                              sx={{ p: 0.25 }}
                            >
                              <KeyboardArrowDown sx={{ fontSize: '0.9rem' }} />
                            </IconButton>
                          </Stack>
                        </Stack>
                      </TableCell>
                    )}

                    {/* Title + specs */}
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="body2"
                            fontWeight="medium"
                            sx={{ fontSize: '0.8rem' }}
                          >
                            {trim.printedText || trim.title}
                            {specLabels.length > 0 && (
                              <Typography
                                component="span"
                                variant="caption"
                                sx={{ opacity: 0.75, ml: 0.5 }}
                              >
                                {specLabels.map((l) => `| ${l}`).join(' ')}
                              </Typography>
                            )}
                          </Typography>
                        </Box>
                        {/* Settings icon for specs */}
                        {hasSpecs && !hideSpecSettings && (
                          <>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                setPopoverAnchorEl(e.currentTarget);
                                setOpenPopoverIdx(idx);
                              }}
                              color={!anySpecSet ? 'error' : 'primary'}
                              sx={{ p: 0.25 }}
                            >
                              <SettingsIcon sx={{ fontSize: '0.9rem' }} />
                            </IconButton>

                            {/* Specs popover */}
                            <Popover
                              open={popoverOpen}
                              anchorEl={popoverAnchorEl}
                              onClose={() => {
                                setOpenPopoverIdx(null);
                                setPopoverAnchorEl(null);
                              }}
                              anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'left',
                              }}
                              transformOrigin={{
                                vertical: 'top',
                                horizontal: 'left',
                              }}
                              disableRestoreFocus
                            >
                              <Box sx={{ p: 2, minWidth: 240, maxWidth: 320 }}>
                                <Typography
                                  variant="subtitle2"
                                  sx={{
                                    fontWeight: 'bold',
                                    mb: 1.5,
                                  }}
                                >
                                  {trim.printedText || trim.title} —
                                  Specifications
                                </Typography>
                                <Stack spacing={1.5}>
                                  {assignedSpecs.map((spec) => (
                                    <SpecPopoverField
                                      key={spec.id}
                                      spec={spec}
                                      value={specValues[spec.id] ?? ''}
                                      onChange={(val) =>
                                        onTrimSpecValueChange(idx, spec.id, val)
                                      }
                                    />
                                  ))}
                                </Stack>
                              </Box>
                            </Popover>
                          </>
                        )}
                      </Stack>
                    </TableCell>

                    {/* Weight / Percentage input */}
                    <TableCell align="center">
                      {isPercentage ? (
                        <TextField
                          type="number"
                          size="small"
                          variant="outlined"
                          value={selection.percentage ?? 0}
                          onChange={(e) =>
                            onTrimPercentageChange(
                              idx,
                              parseFloat(e.target.value) || 0,
                            )
                          }
                          InputProps={{
                            inputProps: {
                              min: 0,
                              max: 100,
                              step: 1,
                              'aria-label': 'Trim percentage',
                            },
                            endAdornment: (
                              <Typography
                                variant="caption"
                                sx={{ ml: 0.25, fontSize: '0.7rem' }}
                              >
                                %
                              </Typography>
                            ),
                          }}
                          sx={{
                            '& .MuiInputBase-input': {
                              fontSize: '0.75rem',
                              p: '4px 8px',
                              textAlign: 'center',
                            },
                            // Hide native number spinner arrows so they don't
                            // overlap short values in the narrow column
                            '& input[type=number]': {
                              MozAppearance: 'textfield',
                            },
                            '& input[type=number]::-webkit-outer-spin-button': {
                              WebkitAppearance: 'none',
                              margin: 0,
                            },
                            '& input[type=number]::-webkit-inner-spin-button': {
                              WebkitAppearance: 'none',
                              margin: 0,
                            },
                          }}
                        />
                      ) : (
                        <TextField
                          type="number"
                          size="small"
                          variant="outlined"
                          value={selection.quantity ?? 0}
                          onChange={(e) =>
                            onTrimWeightChange(
                              idx,
                              parseFloat(e.target.value) || 0,
                            )
                          }
                          inputProps={{
                            min: 0,
                            step: 0.1,
                            'aria-label': 'Trim weight in lbs',
                          }}
                          sx={{
                            '& .MuiInputBase-input': {
                              fontSize: '0.75rem',
                              p: '4px 8px',
                              textAlign: 'center',
                            },
                          }}
                        />
                      )}
                    </TableCell>

                    {/* Remove */}
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => onRemoveTrim(idx)}
                        sx={{ p: 0.25 }}
                      >
                        <Close sx={{ fontSize: '0.9rem' }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}

              {/* Totals row — percentage mode only */}
              {isPercentage && (
                <TableRow>
                  <TableCell>
                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      sx={{ fontSize: '0.75rem' }}
                    >
                      Total
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      sx={{ fontSize: '0.75rem' }}
                      color={totalPercentage === 100 ? 'success.main' : 'error'}
                    >
                      {totalPercentage.toFixed(0)}%
                    </Typography>
                  </TableCell>
                  <TableCell />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Section notes field */}
      {selectedTrimSelections && selectedTrimSelections.length > 0 && (
        <TextField
          fullWidth
          size="small"
          variant="outlined"
          placeholder="Trim notes…"
          value={trimSectionNotes}
          onChange={(e) => onTrimSectionNoteChange(e.target.value)}
          multiline
          maxRows={3}
          sx={{
            '& .MuiInputBase-input': { fontSize: '0.75rem' },
            '& .MuiOutlinedInput-root': {
              borderLeft: '3px solid',
              borderLeftColor: 'text.primary',
              borderRadius: 0,
            },
          }}
          inputProps={{ 'aria-label': 'Trim section notes' }}
        />
      )}

      {/* Available (unselected) trims — click card to add */}
      {trims.length > 0 && (
        <Stack spacing={0.75} sx={{ mt: 1 }}>
          {trims.map((trim) => (
            <Card
              key={trim.id}
              variant="outlined"
              sx={{
                cursor: 'pointer',
                transition: 'background-color 0.15s',
                border: 'none',
                backgroundColor: 'action.hover',
                '&:hover': { backgroundColor: 'action.selected' },
              }}
              onClick={() => onAddTrim(trim.id)}
            >
              <CardHeader
                sx={{ py: 0.75, px: 1.25 }}
                title={
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 'medium' }}>
                    {trim.printedText || trim.title}
                  </Typography>
                }
                action={
                  <Button
                    size="small"
                    variant="text"
                    sx={{
                      fontSize: '0.7rem',
                      py: 0.25,
                      px: 1,
                      minWidth: 'auto',
                      color: 'text.primary',
                      alignSelf: 'center',
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onAddTrim(trim.id);
                    }}
                  >
                    Add
                  </Button>
                }
              />
            </Card>
          ))}
        </Stack>
      )}

      {selectedTrimSelections &&
        selectedTrimSelections.length === 0 &&
        trims.length === 0 && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontStyle: 'italic', fontSize: '0.75rem' }}
          >
            No trim items available
          </Typography>
        )}
    </Box>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

/**
 * AdvancedViewStep
 *
 * Renders the first step of the questionnaire preview as an interactive,
 * editable representation of the PDF cutsheet layout.
 *
 * Layout mirrors `processing-job-cutsheet-request-v2.html`:
 * - Primals arranged in a CSS multi-column grid (`primalColumns`)
 * - Optional specifications sidebar left/right or section top/bottom
 *   (`specsLayoutPosition`)
 * - Every cut has a checkbox so the user can preview include/exclude behaviour
 * - Every primal has an editable notes field
 * @param root0
 * @param root0.printSettings
 * @param root0.disableNonQuestionnaireCuts
 */
export const AdvancedViewStep: FC<AdvancedViewStepProps> = ({
  printSettings: rawSettings,
  disableNonQuestionnaireCuts = true,
  hideSpecSettings = false,
}) => {
  const {
    questionnaire,
    catalog,
    selectedCutIds,
    handleToggleCut: onToggleCut,
    primalNotes,
    handlePrimalNoteChange: onPrimalNoteChange,
    advancedSpecValues: specValues,
    handleAdvancedSpecValueChange: onSpecValueChange,
    cutSpecValues,
    handleCutSpecValueChange: onCutSpecValueChange,
    questionnaireCutIds,
    trimSelections,
    trimMode,
    handleAddTrim: onAddTrim,
    handleRemoveTrim: onRemoveTrim,
    handleMoveTrim: onMoveTrim,
    trimSectionNotes,
    handleTrimSectionNoteChange: onTrimSectionNoteChange,
    handleTrimSpecValueChange: onTrimSpecValueChange,
    handleTrimWeightChange: onTrimWeightChange,
    handleTrimPercentageChange: onTrimPercentageChange,
  } = useQuestionnaireResponse();

  // Merge caller-provided settings with defaults. When the questionnaire has
  // `useCustomPrintSettings` enabled, its own settings fully override the
  // capability's — mirrors `resolvePrintSettings` in generateQuestionnaireResponsePdf.
  const questionnaireOverrides =
    questionnaire.useCustomPrintSettings && questionnaire.printSettings
      ? questionnaire.printSettings
      : {};
  const settings: AdvancedViewPrintSettings = {
    ...DEFAULT_PRINT_SETTINGS,
    ...rawSettings,
    ...questionnaireOverrides,
  };

  const hiddenGroupIds = useMemo(
    () => new Set(settings.hiddenGroupIds),
    [settings.hiddenGroupIds],
  );
  const hiddenItemIds = useMemo(
    () => new Set(settings.hiddenItemIds),
    [settings.hiddenItemIds],
  );
  const hiddenSpecificationIds = useMemo(
    () => new Set(settings.hiddenSpecificationIds),
    [settings.hiddenSpecificationIds],
  );

  // Build a primal → cuts map (O(n) once per render), skipping hidden cuts.
  const cutsByPrimal = useMemo<Record<string, Cut[]>>(() => {
    const map: Record<string, Cut[]> = {};
    for (const cut of catalog.cuts) {
      if (hiddenItemIds.has(cut.id)) continue;
      const key = cut.groupId ?? '__ungrouped__';
      (map[key] ??= []).push(cut);
    }
    return map;
  }, [catalog.cuts, hiddenItemIds]);

  const sortedPrimals = useMemo(
    () =>
      [...catalog.primals]
        .filter((p) => !hiddenGroupIds.has(p.id))
        // Hide primals whose cuts are all hidden — matches PDF behavior
        // (generateQuestionnaireResponsePdf skips groups with empty cuts).
        .filter((p) => (cutsByPrimal[p.id] ?? []).length > 0)
        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)),
    [catalog.primals, hiddenGroupIds, cutsByPrimal],
  );

  const ungroupedCuts = cutsByPrimal.__ungrouped__ ?? [];

  /**
   * Pre-filter the capability's specifications to find which ones are assigned
   * to each specific cut. Avoids O(n*m) filtering inside each CutRow render.
   */
  const specsByCutId = useMemo<Record<string, Specification[]>>(() => {
    const map: Record<string, Specification[]> = {};
    const cutIdSet = new Set(catalog.cuts.map((c) => c.id));
    for (const spec of catalog.specifications) {
      if (!Array.isArray(spec.assignedToIds)) continue;
      for (const id of spec.assignedToIds) {
        if (cutIdSet.has(id)) {
          (map[id] ??= []).push(spec);
        }
      }
    }
    return map;
  }, [catalog.specifications, catalog.cuts]);

  /**
   * Pre-filter specifications assigned to each trim.
   */
  const specsByTrimId = useMemo<Record<string, Specification[]>>(() => {
    const map: Record<string, Specification[]> = {};
    const trimIdSet = new Set(catalog.trim.map((t) => t.id));
    for (const spec of catalog.specifications) {
      if (!Array.isArray(spec.assignedToIds)) continue;
      for (const id of spec.assignedToIds) {
        if (trimIdSet.has(id)) {
          (map[id] ??= []).push(spec);
        }
      }
    }
    return map;
  }, [catalog.specifications, catalog.trim]);

  /**
   * Maps every cut that belongs to a choice-group option to its question and
   * option metadata so we can determine mutual-exclusion conflicts.
   */
  const choiceGroupCutMap = useMemo<
    Record<
      string,
      {
        questionId: string;
        questionTitle: string;
        optionId: string;
        optionLabel: string;
      }
    >
  >(() => {
    const map: Record<
      string,
      {
        questionId: string;
        questionTitle: string;
        optionId: string;
        optionLabel: string;
      }
    > = {};
    for (const q of questionnaire.questions) {
      if (q.type === 'choice_group') {
        const cgq = q as ChoiceGroupQuestion;
        for (const option of cgq.options) {
          for (const cutId of option.cutIds) {
            map[cutId] = {
              questionId: cgq.id,
              questionTitle: cgq.title,
              optionId: option.id,
              optionLabel: option.label,
            };
          }
        }
      }
    }
    return map;
  }, [questionnaire.questions]);

  /**
   * For each currently selected cut that belongs to a choice group, mark all
   * cuts from OTHER options of that group as disabled with an explanation.
   */
  const choiceGroupDisabledCuts = useMemo<Record<string, string>>(() => {
    // Find which option is "active" (has a selected cut) per question
    const activeOptionByQuestion: Record<
      string,
      { optionId: string; optionLabel: string }
    > = {};
    for (const [cutId, membership] of Object.entries(choiceGroupCutMap)) {
      if (selectedCutIds.has(cutId)) {
        activeOptionByQuestion[membership.questionId] = {
          optionId: membership.optionId,
          optionLabel: membership.optionLabel,
        };
      }
    }

    // Disable cuts from non-active options
    const reasons: Record<string, string> = {};
    for (const [cutId, membership] of Object.entries(choiceGroupCutMap)) {
      const active = activeOptionByQuestion[membership.questionId];
      if (active && active.optionId !== membership.optionId) {
        reasons[cutId] =
          `"${active.optionLabel}" is already selected — only one option can be chosen`;
      }
    }
    return reasons;
  }, [choiceGroupCutMap, selectedCutIds]);

  // ── Primals multi-column grid ───────────────────────────────────────────────
  const primalsGrid = (
    <Box
      sx={{
        // CSS multi-column layout — mirrors `.primals-grid { column-count: N }`.
        // Collapse to a single column on mobile for readability.
        columnCount: { xs: 1, md: settings.primalColumns },
        width: { xs: 1, md: 'auto' },
        columnGap: '1rem',
        // Use flex proportional to column count so each "column unit" is equal
        // width — e.g. 2 primal cols + 1 sidebar = flex:2 vs flex:1 → all equal.
        // Only applied at md+ where the sidebar sits alongside the grid.
        flex: {
          md:
            settings.specsLayoutPosition === 'left' ||
            settings.specsLayoutPosition === 'right'
              ? settings.primalColumns
              : undefined,
        },
        minWidth: 0,
      }}
    >
      {sortedPrimals.map((primal) => (
        <Box
          key={primal.id}
          sx={{ breakInside: 'avoid', pageBreakInside: 'avoid' }}
        >
          <PrimalSection
            primal={primal}
            cuts={cutsByPrimal[primal.id] ?? []}
            alwaysShowNotes={settings.alwaysShowPrimalNotes}
            selectedCutIds={selectedCutIds}
            notes={primalNotes[primal.id] ?? ''}
            onToggleCut={onToggleCut}
            onNoteChange={(note) => onPrimalNoteChange(primal.id, note)}
            specsByCutId={specsByCutId}
            cutSpecValues={cutSpecValues}
            onCutSpecValueChange={onCutSpecValueChange}
            questionnaireCutIds={
              disableNonQuestionnaireCuts
                ? (questionnaireCutIds ?? undefined)
                : undefined
            }
            disabledCutReasons={choiceGroupDisabledCuts}
            hideSpecSettings={hideSpecSettings}
          />
        </Box>
      ))}

      {/* Cuts not assigned to any primal group */}
      {ungroupedCuts.length > 0 && (
        <Box sx={{ breakInside: 'avoid', pageBreakInside: 'avoid' }}>
          <PrimalSection
            primal={{
              id: '__ungrouped__',
              capabilityId: '',
              title: 'Other Cuts',
              type: 'primal',
              createdAt: new Date(),
              updatedAt: new Date(),
            }}
            cuts={ungroupedCuts}
            alwaysShowNotes={settings.alwaysShowPrimalNotes}
            selectedCutIds={selectedCutIds}
            notes={primalNotes.__ungrouped__ ?? ''}
            onToggleCut={onToggleCut}
            onNoteChange={(note) => onPrimalNoteChange('__ungrouped__', note)}
            specsByCutId={specsByCutId}
            cutSpecValues={cutSpecValues}
            onCutSpecValueChange={onCutSpecValueChange}
            questionnaireCutIds={
              disableNonQuestionnaireCuts
                ? (questionnaireCutIds ?? undefined)
                : undefined
            }
            disabledCutReasons={choiceGroupDisabledCuts}
            hideSpecSettings={hideSpecSettings}
          />
        </Box>
      )}
    </Box>
  );

  // ── Shared element builders ──────────────────────────────────────────────────

  const specsEl = (
    <SpecsSidebar
      catalog={catalog}
      specValues={specValues}
      onSpecValueChange={onSpecValueChange}
      hiddenSpecificationIds={hiddenSpecificationIds as Set<string>}
    />
  );

  /** Trim without its own top divider — used when the parent provides separation. */
  const trimEl = (
    <TrimSection
      trims={catalog.trim}
      selectedTrimSelections={trimSelections?.trim}
      mode={trimMode}
      onAddTrim={onAddTrim}
      onRemoveTrim={onRemoveTrim}
      onMoveTrim={onMoveTrim}
      trimSectionNotes={trimSectionNotes}
      onTrimSectionNoteChange={onTrimSectionNoteChange}
      specsByTrimId={specsByTrimId}
      onTrimSpecValueChange={onTrimSpecValueChange}
      onTrimWeightChange={onTrimWeightChange}
      onTrimPercentageChange={onTrimPercentageChange}
      showDivider={false}
      hideSpecSettings={hideSpecSettings}
    />
  );

  // ── Layout based on specsLayoutPosition ────────────────────────────────────

  /**
   * top / bottom: specs and trim sit side-by-side in a 50/50 row —
   * mirrors `.specs-trim-extras-container` in the PDF template.
   */
  const specsTrimRowEl = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        gap: 2,
        alignItems: 'flex-start',
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>{specsEl}</Box>
      {catalog.trim.length > 0 && (
        <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>{trimEl}</Box>
      )}
    </Box>
  );

  if (settings.specsLayoutPosition === 'top') {
    return (
      <Stack spacing={2}>
        {specsTrimRowEl}
        <Divider />
        {primalsGrid}
      </Stack>
    );
  }

  if (settings.specsLayoutPosition === 'bottom') {
    return (
      <Stack spacing={2}>
        {primalsGrid}
        <Divider />
        {specsTrimRowEl}
      </Stack>
    );
  }

  /**
   * left / right: trim lives inside the sidebar, stacked below specs —
   * mirrors `.specs-sidebar` in the PDF template.
   */
  const sidebarEl = (
    <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
      {specsEl}
      {catalog.trim.length > 0 && (
        <Box sx={{ mt: 1.5 }}>
          <Divider sx={{ mb: 1.5 }} />
          {trimEl}
        </Box>
      )}
    </Box>
  );

  if (settings.specsLayoutPosition === 'left') {
    return (
      <Stack spacing={2}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
            alignItems: 'flex-start',
          }}
        >
          {sidebarEl}
          <Divider
            orientation="vertical"
            flexItem
            sx={{ display: { xs: 'none', md: 'block' } }}
          />
          {primalsGrid}
        </Box>
      </Stack>
    );
  }

  if (settings.specsLayoutPosition === 'right') {
    return (
      <Stack spacing={2}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
            alignItems: 'flex-start',
          }}
        >
          {primalsGrid}
          <Divider
            orientation="vertical"
            flexItem
            sx={{ display: { xs: 'none', md: 'block' } }}
          />
          {sidebarEl}
        </Box>
      </Stack>
    );
  }

  // 'none' — no specs section; trim falls below the primals grid with its own divider
  return (
    <Stack spacing={0}>
      {primalsGrid}
      {catalog.trim.length > 0 && (
        <TrimSection
          trims={catalog.trim}
          selectedTrimSelections={trimSelections?.trim}
          mode={trimMode}
          onAddTrim={onAddTrim}
          onRemoveTrim={onRemoveTrim}
          onMoveTrim={onMoveTrim}
          trimSectionNotes={trimSectionNotes}
          onTrimSectionNoteChange={onTrimSectionNoteChange}
          specsByTrimId={specsByTrimId}
          onTrimSpecValueChange={onTrimSpecValueChange}
          onTrimWeightChange={onTrimWeightChange}
          onTrimPercentageChange={onTrimPercentageChange}
          showDivider
          hideSpecSettings={hideSpecSettings}
        />
      )}
    </Stack>
  );
};
